import { defineAsyncComponent } from 'vue'

import {
  BRANDED_KEYWORD_DROPDOWNS,
  BRANDED_CAMPAIGN_DROPDOWNS,
  BRANDED_SEARCHTERM_DROPDOWNS,
  KEYWORD_PERFORMANCE_TOP_TABS,
  ORGANIC_BRANDED_DROPDOWN,
  ORGANIC_POSITION_DROPDOWN,
  SEARCH_TERM_TOP_TABS,
  campaignOverviewBuild,
  extensionsBuild,
  keywordLabelsBuild,
  keywordPerformanceBuild,
  keywordQualityScoresBuild,
  organicKeywordsBuild,
  organicTrendlineBuild,
  paidOrganicBuild,
  qualityScoreElementsBuild,
  searchTermsBuild,
} from './searchAdsExcellence.builds'

import { DashboardChartArea } from '@/features/dashboards'
import type { ReportLayoutConfig } from '@/features/reports'

// The layout tree only — builds/data maps live in `searchAdsExcellence.builds.ts` (400-line
// file gate). See that file's header for the porting notes.
//
// Moved out of 08's `SEED_REPORT_LAYOUTS` by ORP-18922 under the handoff protocol in
// `reports/configs/layouts.ts` — the vertical owns the whole key so the Temporal Heatmap section
// can carry a real `component`. Dashboard trendlines use module 09's chart-area renderer; the
// n-gram renderers and KeywordTrends keep their existing feature-owned follow-up seams.

export const SEARCH_ADS_EXCELLENCE: ReportLayoutConfig = [
  {
    name: 'Keywords',
    labelKey: 'reports.searchAdsExcellence.tabs.keywords',
    sections: [
      {
        id: '71-campaign-overview',
        reportId: 71,
        renderer: DashboardChartArea,
        rendererMode: 'replace',
        rendererProps: {
          excludeMetrics: ['reach'],
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: campaignOverviewBuild,
        tabs: [
          {
            name: 'Performance Trendline',
            tabs: ['overview'],
            channel: 'google_ads',
            dropdowns: BRANDED_CAMPAIGN_DROPDOWNS,
          },
        ],
      },
      {
        id: '71-keyword-performance',
        titleKey: 'reports.searchAdsExcellence.keywordPerformance.title',
        reportId: 71,
        build: keywordPerformanceBuild,
        topTabs: KEYWORD_PERFORMANCE_TOP_TABS,
        tabs: [
          { name: 'Overall', tabs: ['overview'], dropdowns: BRANDED_KEYWORD_DROPDOWNS },
          {
            name: 'Google',
            tabs: ['google_ads_keyword'],
            channel: 'google_ads',
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
          {
            name: 'Apple',
            tabs: ['apple_search_ads_keyword'],
            channel: 'apple_search_ads',
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
          {
            name: 'Yandex',
            tabs: ['yandex_ads_keyword'],
            channel: 'yandex_ads',
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
        ],
      },
      {
        id: '71-keyword-quality-scores',
        titleKey: 'reports.searchAdsExcellence.keywordQualityScores.title',
        hideChannels: true,
        reportId: 71,
        build: keywordQualityScoresBuild,
        tabs: [
          {
            name: 'Keyword Quality Scores',
            tabs: ['google_ads_keyword'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
        ],
      },
      {
        id: '71-quality-score-elements',
        titleKey: 'reports.searchAdsExcellence.qualityScoreElements.title',
        topTabMode: true,
        reportId: 71,
        build: qualityScoreElementsBuild,
        tabs: [
          {
            name: 'Keyword Match Type',
            labelKey: 'reports.searchAdsExcellence.qualityScoreElements.keywordMatchType',
            tabs: ['google_ads_keyword'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
          {
            name: 'Ad Relevance Distribution',
            labelKey: 'reports.searchAdsExcellence.qualityScoreElements.adRelevance',
            tabs: ['google_ads_keyword'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
        ],
      },
      {
        id: '1-campaign-heatmap',
        // The section title travels INTO the component: a registrant-owned renderer replaces the
        // whole config-driven section, chrome included (`ReportPage.vue:196-197`).
        reportId: 1,
        component: defineAsyncComponent(
          () => import('../components/sections/SearchAdsHourlyHeatmap.vue'),
        ),
        tabs: [{ name: '', tabs: ['searchads_hourly'] }],
      },
      {
        id: '71-keyword-labels',
        titleKey: 'reports.searchAdsExcellence.keywordLabels.title',
        checkShow: true,
        topTabMode: true,
        reportId: 71,
        build: keywordLabelsBuild,
        tabs: [
          {
            name: 'Category',
            labelKey: 'reports.searchAdsExcellence.keywordLabels.category',
            checkCols: ['category_label'],
            tabs: ['overview'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
          {
            name: 'Brand',
            labelKey: 'reports.searchAdsExcellence.keywordLabels.brand',
            checkCols: ['brand_label'],
            tabs: ['overview'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
          {
            name: 'Color',
            labelKey: 'reports.searchAdsExcellence.keywordLabels.color',
            checkCols: ['color_label'],
            tabs: ['overview'],
            dropdowns: BRANDED_KEYWORD_DROPDOWNS,
          },
        ],
      },
    ],
  },
  {
    name: 'Paid & Organic',
    labelKey: 'reports.searchAdsExcellence.tabs.paidOrganic',
    sections: [
      {
        id: '5-paid-organic',
        hideChannels: true,
        reportId: 5,
        renderer: DashboardChartArea,
        rendererMode: 'prepend',
        rendererProps: {
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: paidOrganicBuild,
        tabs: [{ name: 'Performance Breakdown', reportId: 5, tabs: ['overview'] }],
      },
    ],
  },
  {
    name: 'Organic Keywords',
    labelKey: 'reports.searchAdsExcellence.tabs.organicKeywords',
    sections: [
      {
        id: '5-performance-trendline',
        reportId: 5,
        hideChannels: true,
        renderer: DashboardChartArea,
        rendererMode: 'replace',
        rendererProps: {
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: organicTrendlineBuild,
        tabs: [
          {
            name: 'Performance Trendline',
            tabs: ['False_branded_keyword'],
            dropdowns: [ORGANIC_BRANDED_DROPDOWN],
          },
        ],
      },
      {
        id: '5-performance-breakdown',
        topTabMode: true,
        build: organicKeywordsBuild,
        reportId: 5,
        tabs: [
          {
            name: 'Position',
            labelKey: 'reports.searchAdsExcellence.organicKeywords.position',
            tabs: ['False_branded_keyword'],
            dropdowns: [ORGANIC_BRANDED_DROPDOWN, ORGANIC_POSITION_DROPDOWN],
          },
          {
            name: 'Short Tail Keywords',
            labelKey: 'reports.searchAdsExcellence.organicKeywords.shortTail',
            tabs: ['False_branded_keyword'],
            dropdowns: [ORGANIC_BRANDED_DROPDOWN],
          },
          {
            name: 'Long Tail Keywords',
            labelKey: 'reports.searchAdsExcellence.organicKeywords.longTail',
            tabs: ['False_branded_keyword'],
            dropdowns: [ORGANIC_BRANDED_DROPDOWN],
          },
          {
            name: 'Landing Page',
            labelKey: 'reports.searchAdsExcellence.organicKeywords.landingPage',
            channel: 'search_console',
            tabs: ['metrics_organic_keyword_landingpage'],
          },
        ],
      },
      {
        id: '5-keyword-performance',
        kind: 'pending',
        pendingNote: 'v3 isNgram → NgramChartArea (follow-up slice)',
        titleKey: 'reports.searchAdsExcellence.organicKeywords.keywordPerformance',
        hideChannels: true,
        reportId: 5,
        tabs: [{ name: 'Keyword Performance', kind: 'pending', tabs: ['organic_ngram_data'] }],
      },
    ],
  },
  {
    name: 'Search Terms',
    labelKey: 'reports.searchAdsExcellence.tabs.searchTerms',
    sections: [
      {
        id: '6-ngram-overview',
        kind: 'pending',
        pendingNote: 'v3 isNgram → NgramChartArea (follow-up slice)',
        titleKey: 'reports.searchAdsExcellence.searchTerms.overview',
        hideChannels: true,
        reportId: 5,
        tabs: [
          {
            name: 'Overview',
            kind: 'pending',
            tabs: ['True_branded_ngram', 'False_branded_ngram'],
          },
        ],
      },
      {
        id: '5-search-terms',
        checkShow: true,
        reportId: 5,
        build: searchTermsBuild,
        topTabs: SEARCH_TERM_TOP_TABS,
        tabs: [
          {
            name: 'Overall',
            tabs: ['metrics_searchterm'],
            dropdowns: BRANDED_SEARCHTERM_DROPDOWNS,
          },
          {
            name: 'Google',
            channel: 'google_ads',
            tabs: ['google_ads_searchterm'],
            dropdowns: BRANDED_SEARCHTERM_DROPDOWNS,
          },
          {
            name: 'Yandex',
            channel: 'yandex_ads',
            tabs: ['yandex_ads_searchterm'],
            dropdowns: BRANDED_SEARCHTERM_DROPDOWNS,
          },
          {
            name: 'Apple',
            channel: 'apple_search_ads',
            tabs: ['apple_search_ads_searchterm'],
            dropdowns: BRANDED_SEARCHTERM_DROPDOWNS,
          },
        ],
      },
    ],
  },
  {
    name: 'Extensions',
    labelKey: 'reports.searchAdsExcellence.tabs.extensions',
    sections: [
      {
        id: '1-extension',
        titleKey: 'reports.searchAdsExcellence.extensions.title',
        reportId: 1,
        topTabMode: true,
        build: extensionsBuild,
        tabs: [
          {
            name: 'Sitelink',
            labelKey: 'reports.searchAdsExcellence.extensions.sitelink',
            tabs: ['SITELINK_extension'],
          },
          {
            name: 'Callout',
            labelKey: 'reports.searchAdsExcellence.extensions.callout',
            tabs: ['CALLOUT_extension'],
          },
        ],
      },
    ],
  },
  {
    name: 'Keyword Trends',
    labelKey: 'reports.searchAdsExcellence.tabs.keywordTrends',
    selectablePresets: ['Last 30 Days'],
    defaultPreset: 'Last 30 Days',
    sections: [
      {
        id: '1-keyword-trends',
        // Registrant-owned renderer: it carries its own chart/table toggle, so the section title
        // travels into the component and every other field here is ignored (`ReportPage.vue`).
        reportId: 1,
        component: defineAsyncComponent(
          () => import('../components/sections/SearchAdsKeywordTrends.vue'),
        ),
        tabs: [{ name: '', tabs: ['metrics_tsdp_keywords'] }],
      },
    ],
  },
]
